import { useEffect, useState } from "react";
import { jobsApi, tasksApi, ApiError } from "@/api";
import type { JobListRow, TaskRow, TaskCreateInput, TaskUpdateInput } from "@/types";
import { Button, Spinner, TextField, TextAreaField, SelectField } from "@/components/ui";
import { humanise } from "@/lib/jobs";
import { toDateInputValue } from "@/lib/format";
import { FormDrawer } from "@/staff/jobs/FormDrawer";
import { OwnerSelect, useStaffUsers } from "./OwnerSelect";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["open", "in_progress", "blocked", "done", "cancelled"];

/** Create-mode seed for callers that already know the context (e.g. "create
 *  task from a chat message") — fixes the job (read-only, no picker) and
 *  optionally pre-fills the form and links the source message. */
export interface TaskDrawerInitial {
  job: { id: number; label: string };
  title?: string;
  description?: string;
  chatMessageId?: number;
}

/** Create or edit a task from the cross-job dashboard. Create can optionally
 *  link a job via search (job_id is create-time only — the update endpoint
 *  doesn't accept it, so edit shows the job read-only). On success the parent
 *  refetches the working set. */
export function TaskDrawer({
  task,
  initial,
  onClose,
  onSaved,
}: {
  task: TaskRow | null; // null = create
  initial?: TaskDrawerInitial; // create only — ignored when editing
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = task != null;
  const seeded = !isEdit ? initial : undefined;
  const [title, setTitle] = useState(task?.title ?? seeded?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? seeded?.description ?? "");
  const [priority, setPriority] = useState(task?.priority || "medium");
  const [status, setStatus] = useState(task?.status || "open");
  const [due, setDue] = useState(toDateInputValue(task?.due_date));
  const [notes, setNotes] = useState(task?.notes ?? "");
  const [ownerId, setOwnerId] = useState<number>(task?.assigned_to_user_id ?? 0);
  const { staff, failed: staffFailed } = useStaffUsers();

  // Optional job link (create only).
  const [picked, setPicked] = useState<JobListRow | null>(null);
  const [jobQuery, setJobQuery] = useState("");
  const [jobResults, setJobResults] = useState<JobListRow[]>([]);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function del() {
    if (!task || saving || deleting) return;
    if (!window.confirm(`Delete the task “${task.title}”? This can't be undone.`)) return;
    setDeleting(true);
    setError(undefined);
    try {
      await tasksApi.updateTask({ id: task.id, deleted: true });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the task.");
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (isEdit || seeded || picked) return;
    const term = jobQuery.trim();
    if (term.length < 2) {
      setJobResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      jobsApi
        .listJobs({ q: term, per_page: 20, sort_field: "job_number", sort_direction: "desc" })
        .then((res) => {
          if (!cancelled) setJobResults(res.items ?? []);
        })
        .catch(() => {
          if (!cancelled) setJobResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [jobQuery, isEdit, picked]);

  async function submit() {
    if (saving) return;
    if (!title.trim()) {
      setError("Give the task a title.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      if (isEdit && task) {
        const body: TaskUpdateInput = {
          id: task.id,
          title: title.trim(),
          description: description.trim(),
          priority,
          status,
          due_date: due || null,
          notes: notes.trim(),
        };
        // Owner is only touched once the roster loaded — a failed load must
        // never clear an existing assignment.
        if (staff != null) {
          if (ownerId > 0) body.assigned_to_user_id = ownerId;
          else if (task.assigned_to_user_id) body.unassign = true;
        }
        await tasksApi.updateTask(body);
      } else {
        const input: TaskCreateInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          priority,
          due_date: due || null,
          notes: notes.trim() || undefined,
        };
        if (staff != null && ownerId > 0) input.assigned_to_user_id = ownerId;
        if (seeded) {
          input.job_id = seeded.job.id;
          if (seeded.chatMessageId) input.chat_message_id = seeded.chatMessageId;
        } else if (picked) {
          input.job_id = picked.id;
        }
        await tasksApi.createTask(input);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the task.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={isEdit ? "Edit task" : "New task"}
      busy={saving || deleting}
      onClose={onClose}
      footer={
        <>
          {isEdit ? (
            <Button variant="danger" className="job-task-del" onClick={del} loading={deleting} disabled={saving}>Delete task</Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={deleting}>{isEdit ? "Save task" : "Add task"}</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <TextField label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextAreaField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="lead-drawer__two">
          <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{humanise(p)}</option>)}
          </SelectField>
          {isEdit ? (
            <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </SelectField>
          ) : null}
        </div>
        <div className="lead-drawer__two">
          <TextField label="Due date" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          <OwnerSelect staff={staff} failed={staffFailed} value={ownerId} onChange={setOwnerId} />
        </div>
      </section>

      <section className="job-drawer__section">
        <h3>Job</h3>
        {isEdit ? (
          <p className="job-drawer__muted">
            {task && task.job_id
              ? `#${task.job_number} · ${task.job_name || "Untitled job"} (set at create — not changeable here)`
              : "Not linked to a job."}
          </p>
        ) : seeded ? (
          <p className="job-drawer__muted">{seeded.job.label}</p>
        ) : picked ? (
          <div className="job-client-picked">
            <div><strong className="numeric">#{picked.job_number}</strong> · {picked.job_name || "Untitled job"}</div>
            <Button variant="ghost" size="sm" onClick={() => { setPicked(null); setJobQuery(""); }}>Change</Button>
          </div>
        ) : (
          <div className="job-client-picker">
            <input
              className="leads__search"
              type="search"
              placeholder="Search a job to link (optional)…"
              value={jobQuery}
              onChange={(e) => setJobQuery(e.target.value)}
            />
            {searching ? <div className="app-letters__loading"><Spinner label="Searching jobs…" /></div> : null}
            {!searching && jobQuery.trim().length >= 2 && jobResults.length === 0 ? (
              <p className="job-drawer__muted">No jobs found.</p>
            ) : null}
            {jobResults.length > 0 ? (
              <ul className="job-client-results">
                {jobResults.map((j) => (
                  <li key={j.id}>
                    <button type="button" className="job-client-result" onClick={() => { setPicked(j); setJobResults([]); }}>
                      <span className="job-client-result__name"><span className="numeric">#{j.job_number}</span> · {j.job_name || "Untitled job"}</span>
                      <span className="job-client-result__email">{j.client_display || "No client"}{j.site_full_address ? ` · ${j.site_full_address}` : ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </section>

      <section className="job-drawer__section">
        <h3>Notes</h3>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>
    </FormDrawer>
  );
}
